import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, Button, Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ResetPasswordReq } from './api/Application';
import { Api } from './api/Api';

export function ResetPasswordPage() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const resetCode = searchParams.get( 'code' ) || '';
    const [email, setEmail]       = useState( '' );
    const [checking, setChecking] = useState( true );
    const [saving, setSaving]     = useState( false );
    const [error, setError]       = useState( '' );
    const [success, setSuccess]   = useState( false );

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm<ResetPasswordReq & { confirmPassword: string }>( {
        defaultValues: { resetCode, email: '', newPassword: '', confirmPassword: '' }
    } );

    const password = watch( 'newPassword' );
    useEffect( () => {
        if( !resetCode ) { nav( '/forgot' ); return; }
        Api.resetPasswordCheck( { resetCode } ).then( result => {
            if( result.isOk ) setEmail( result.value.emailAddress );
            else               nav( '/forgot' );
            setChecking( false );
        } );
    }, [resetCode, nav] );

    const onSubmit = async ( data: ResetPasswordReq & { confirmPassword: string } ) => {
        setSaving( true );
        setError( '' );
        const result = await Api.resetPassword( { ...data, email } );
        if( result.isOk ) setSuccess( true );
        else               setError( result.error );
        setSaving( false );
    };

    if( checking ) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Verifying reset code...</p>
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <Row className="justify-content-center">
                <Col md={6}>
                    <Card>
                        <Card.Header as="h4" className="text-center">Reset Password</Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}

                            {success ? (
                                <Alert variant="success">
                                    <p>Your password has been successfully reset!</p>
                                    <div className="text-center mt-3">
                                        <Link to="/login">
                                            <Button variant="primary">Login with New Password</Button>
                                        </Link>
                                    </div>
                                </Alert>
                            ) : (
                                <Form onSubmit={handleSubmit( onSubmit )}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Enter new password"
                                            {...register( 'newPassword', {
                                                required: 'Password is required',
                                                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                                                pattern: {
                                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                                    message: 'Password must include uppercase, lowercase, number and special character'
                                                }
                                            } )}
                                            isInvalid={!!errors.newPassword}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.newPassword?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Confirm your new password"
                                            {...register( 'confirmPassword', {
                                                validate: value => value === password || 'Passwords do not match'
                                            } )}
                                            isInvalid={!!errors.confirmPassword}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.confirmPassword?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Control type="hidden" {...register( 'resetCode' )} />

                                    <div className="d-grid gap-2">
                                        <Button variant="primary" type="submit" disabled={saving}>
                                            {saving ? 'Resetting Password...' : 'Reset Password'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}